import { useFeatureFlag } from '@src/hooks';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { MixpanelEvent, MixpanelEventProperty, Toggle, useMixpanel, type ToggleProps } from '@talos/kyoko';
import { useCallback, type SetStateAction } from 'react';
import type { BlotterTableOrderFilter } from './useOrderFilter';

interface OrderSettingsTogglesProps extends Pick<ToggleProps, 'size' | 'fullWidth'> {
  filter: BlotterTableOrderFilter;
  changeFilter: (action: SetStateAction<BlotterTableOrderFilter>) => void;
  showAPIOrdersToggle?: boolean;
  alwaysShowDDHOrders?: boolean;
}

export function OrderSettingsToggles({
  filter,
  changeFilter,
  showAPIOrdersToggle = true,
  alwaysShowDDHOrders,
  size,
  fullWidth,
}: OrderSettingsTogglesProps) {
  const mixpanel = useMixpanel();
  const { enableArchiveOrders, enableDynamicDeltaHedging } = useFeatureFlag();
  const { showAllDecimalsInBlotters, setShowAllDecimalsInBlotters } = useTradingSettings();

  const handleApiOrdersChange = useCallback(
    (value: boolean) => {
      mixpanel.track(MixpanelEvent.ApiOrdersToggle, { [MixpanelEventProperty.Enabled]: value });
      changeFilter(curr => ({ ...curr, HideApiCalls: !value }));
    },
    [changeFilter, mixpanel]
  );

  const handleShowArchivedOrdersChange = useCallback(
    (value: boolean) => {
      mixpanel.track(MixpanelEvent.ShowArchiveOrdersToggle, { [MixpanelEventProperty.Enabled]: value });
      changeFilter(curr => ({ ...curr, ShowArchivedOrders: value }));
    },
    [changeFilter, mixpanel]
  );

  const handleShowDDHOrdersChange = useCallback(
    (value: boolean) => {
      mixpanel.track(
        value ? MixpanelEvent.DynamicDeltaHedgingOrdersShown : MixpanelEvent.DynamicDeltaHedgingOrdersHidden
      );
      changeFilter(curr => ({ ...curr, ShowDDHOrders: value }));
    },
    [changeFilter, mixpanel]
  );

  const handleShowAllDecimalsChange = useCallback(
    (value: boolean) => {
      mixpanel.track(MixpanelEvent.ShowAllDecimalsInBlotters, {
        [MixpanelEventProperty.Enabled]: value,
      });
      setShowAllDecimalsInBlotters(value);
    },
    [mixpanel, setShowAllDecimalsInBlotters]
  );

  return (
    <>
      {showAPIOrdersToggle ? (
        <Toggle
          label="API Orders"
          size={size}
          tooltip="Include orders submitted through the API in the results."
          checked={!filter.HideApiCalls}
          onChange={handleApiOrdersChange}
          fullWidth={fullWidth}
        />
      ) : null}
      {enableArchiveOrders ? (
        <Toggle
          label="Show Archived Orders"
          size={size}
          tooltip="Show orders which have been archived."
          checked={filter.ShowArchivedOrders === true}
          onChange={handleShowArchivedOrdersChange}
          fullWidth={fullWidth}
        />
      ) : null}
      {enableDynamicDeltaHedging && !alwaysShowDDHOrders ? (
        <Toggle
          label="Show DDH Orders"
          size={size}
          tooltip="Show Order Level DDH Orders."
          checked={filter.ShowDDHOrders === true}
          onChange={handleShowDDHOrdersChange}
          fullWidth={fullWidth}
        />
      ) : null}
      <Toggle
        label="Show All Decimals"
        size={size}
        checked={showAllDecimalsInBlotters}
        onChange={handleShowAllDecimalsChange}
        fullWidth={fullWidth}
      />
    </>
  );
}
